import { computed } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import {
  ADMINISTRATIVE_ROOT_PATH,
  isNavbarItemActive,
  matchesReturnToSection,
  practiceDetailIdOf,
  type NavbarItem,
} from "@/components/shared/navbar/useNavbarItems.ts";
import { useTreatmentPlanActionableCounts } from "@/composables/models/treatmentPlan/useTreatmentPlan.ts";
import {
  COTREATMENT_ACCENT,
  REFERRAL_ACCENT,
} from "@/composables/models/patient/usePatientDashboard.ts";
import {
  DSO_REPRESENTATIVE_PATH,
  DSO_TEAM_PATH,
  isDsoRepresentativePath,
  isDsoTeamPath,
  ORGANIZATION_DETAIL_PREFIXES,
  ORGANIZATION_LEVEL_PATHS,
  ORGANIZATION_ROOT_PATH,
  useMyOrganization,
} from "@/composables/shared/useMyOrganization";
import { useCanAccessDsoRepresentative } from "@/composables/shared/useSectionPermissions";
import {
  billingPagePath,
  billingScopeOfOwner,
  canOpenBillingPage,
  canScopeOpenBillingPage,
  isBillingPath,
  readBillingOwnerQuery,
  readBillingRights,
  type TBillingOwnerQuery,
  type TBillingPage,
} from "@/composables/shared/billingAccess";
import { useAbility } from "@/api/models/auth/ability";

type NavbarSection = "dashboard" | "organization" | "billing";

export const BILLING_ROOT_PATH = "/billing";

export const BILLING_ACCENTS = {
  general: "#45478c",
  invoices: "#009c89",
  transactions: "#3d7ef0",
  intervals: "#f5a53a",
  shop: "#b56cff",
  paymentMethods: "#b56cff",
  payouts: "#e0568a",
} as const;

/**
 * Two ramps off the DSO plum (257°): practices/offices run warm, team/representative run cool.
 */
export const ORGANIZATION_ACCENTS = {
  dso: "#1b1725",
  practices: "#8b4557",
  offices: "#c1494d",
  dsoRepresentative: "#00777f",
  team: "#45478c",
} as const;

export function useNavbarSubItems() {
  const route = useRoute();
  const { t } = useI18n();

  const { data: actionableCounts, isLoading: isActionableCountsLoading } =
    useTreatmentPlanActionableCounts();

  const { hasLevel } = useMyOrganization();
  const canAccessDsoRepresentative = useCanAccessDsoRepresentative();
  const { can } = useAbility();
  const canReadStaff = computed(() =>
    can($permActions.READ, $permSubjects.PRACTICE__PRACTICESTAFF)
  );

  const billingRights = computed(() => readBillingRights(can));

  const billingOwnerQuery = computed<TBillingOwnerQuery>(() => {
    if (isBillingPath(route.path)) return readBillingOwnerQuery(route.query);

    const practiceId = billingRights.value.canCompanyBilling
      ? practiceDetailIdOf(route)
      : undefined;
    return practiceId ? { company: practiceId } : {};
  });

  const billingScope = computed(() =>
    billingScopeOfOwner(billingRights.value, billingOwnerQuery.value)
  );

  const canOpenBillingItem = (page: TBillingPage) =>
    billingScope.value
      ? canScopeOpenBillingPage(billingRights.value, billingScope.value, page)
      : canOpenBillingPage(billingRights.value, page);

  const billingItem = (
    page: TBillingPage,
    item: Omit<NavbarItem, "url" | "to" | "hidden">
  ): NavbarItem => {
    const url = billingPagePath(page);
    return {
      ...item,
      url,
      to: { path: url, query: billingOwnerQuery.value },
      hidden: !canOpenBillingItem(page),
    };
  };

  const sections = computed<Record<NavbarSection, NavbarItem[]>>(() => ({
    dashboard: [
      {
        title: t("generic.navbar.all"),
        url: "/dashboard/all-patients",
        icon: "mdi-account-multiple-outline",
      },
      {
        title: t("generic.fields.treatmentTypes.cotreatment"),
        url: "/dashboard/co-treatment",
        dividerBefore: true,
        badgeNumber: actionableCounts.value?.cotreatment,
        badgeColor: COTREATMENT_ACCENT,
        accentColor: COTREATMENT_ACCENT,
        icon: "mdi-account-group-outline",
      },
      {
        title: t("generic.entity.referral", 2),
        url: "/dashboard/referrals",
        badgeNumber: actionableCounts.value?.referral,
        badgeColor: REFERRAL_ACCENT,
        accentColor: REFERRAL_ACCENT,
        icon: "mdi-send-outline",
      },
    ],
    billing: [
      billingItem("general", {
        title: t("generic.fields.sections.general"),
        icon: "mdi-view-dashboard-outline",
        accentColor: BILLING_ACCENTS.general,
      }),
      billingItem("invoices", {
        title: t("generic.fields.sections.invoices"),
        icon: "mdi-receipt-text-outline",
        accentColor: BILLING_ACCENTS.invoices,
        dataTour: "tour-billing-tab-invoices",
      }),
      billingItem("payment-methods", {
        title: t("generic.fields.sections.paymentMethods"),
        icon: "mdi-credit-card-outline",
        accentColor: BILLING_ACCENTS.paymentMethods,
      }),
      billingItem("payouts", {
        title: t("generic.fields.sections.payouts"),
        icon: "mdi-cash-fast",
        accentColor: BILLING_ACCENTS.payouts,
      }),
      billingItem("transactions", {
        title: t("generic.fields.sections.transactionHistory"),
        icon: "mdi-swap-horizontal",
        accentColor: BILLING_ACCENTS.transactions,
      }),
      billingItem("intervals", {
        title: t("features.billing.intervals.title"),
        icon: "mdi-calendar-clock-outline",
        accentColor: BILLING_ACCENTS.intervals,
      }),
    ],
    organization: [
      {
        title: t("generic.entity.dso"),
        url: ORGANIZATION_LEVEL_PATHS.dso,
        icon: "mdi-domain",
        accentColor: ORGANIZATION_ACCENTS.dso,
        activeMatch: (path) =>
          (path === ORGANIZATION_LEVEL_PATHS.dso || path.startsWith("/msos/")) &&
          !isDsoRepresentativePath(path) &&
          !isDsoTeamPath(path),
        hidden: !hasLevel("dso"),
      },
      {
        title: t("generic.entity.company", 2),
        url: ORGANIZATION_LEVEL_PATHS.practices,
        activePrefix: "/companies",
        icon: "mdi-office-building-outline",
        accentColor: ORGANIZATION_ACCENTS.practices,
        dataTour: "tour-subnav-practices",
      },
      {
        title: t("generic.entity.practice", 2),
        url: ORGANIZATION_LEVEL_PATHS.offices,
        activePrefix: "/practices",
        icon: "mdi-hospital-building",
        accentColor: ORGANIZATION_ACCENTS.offices,
        dataTour: "tour-subnav-offices",
      },
      {
        title: t("generic.fields.sections.persons"),
        url: DSO_TEAM_PATH,
        icon: "mdi-account-supervisor-outline",
        accentColor: ORGANIZATION_ACCENTS.team,
        activeMatch: (path) => path === DSO_TEAM_PATH || isDsoTeamPath(path),
        hidden: !hasLevel("dso") || !canReadStaff.value,
      },
      {
        title: t("models.mso.nav.dsoRepresentative"),
        url: DSO_REPRESENTATIVE_PATH,
        icon: "mdi-account-tie-outline",
        accentColor: ORGANIZATION_ACCENTS.dsoRepresentative,
        activeMatch: (path) => path === DSO_REPRESENTATIVE_PATH || isDsoRepresentativePath(path),
        hidden: !canAccessDsoRepresentative.value || !hasLevel("dso"),
      },
    ],
  }));

  const activeSection = computed<NavbarSection | undefined>(() => {
    if (route.path === "/dashboard" || route.path.startsWith("/dashboard/")) {
      return "dashboard";
    }

    const returnTo = route.query.returnTo as string | undefined;
    const isOrganizationPath =
      !matchesReturnToSection(returnTo, ADMINISTRATIVE_ROOT_PATH) &&
      [ORGANIZATION_ROOT_PATH, ...ORGANIZATION_DETAIL_PREFIXES].some(
        (prefix) => route.path === prefix || route.path.startsWith(`${prefix}/`)
      );
    if (isOrganizationPath) {
      return "organization";
    }

    if (isBillingPath(route.path)) {
      return "billing";
    }

    return undefined;
  });

  const subNavItems = computed(() => {
    if (!activeSection.value) {
      return [];
    }

    return sections.value[activeSection.value].filter((item) => !item.hidden);
  });

  const sectionRootUrls: Record<NavbarSection, string> = {
    dashboard: "/",
    organization: ORGANIZATION_ROOT_PATH,
    billing: BILLING_ROOT_PATH,
  };

  const activeSectionRootUrl = computed(() =>
    activeSection.value ? sectionRootUrls[activeSection.value] : undefined
  );

  const sectionsByRootUrl = computed<Record<string, NavbarItem[]>>(() =>
    Object.fromEntries(
      (Object.keys(sections.value) as NavbarSection[]).map((key) => [
        sectionRootUrls[key],
        sections.value[key].filter((item) => !item.hidden),
      ])
    )
  );

  return {
    activeSection,
    activeSectionRootUrl,
    subNavItems,
    sectionsByRootUrl,
    isActionableCountsLoading,
  };
}

export function useActiveSubNavTitle() {
  const route = useRoute();
  const { subNavItems } = useNavbarSubItems();

  return computed(
    () =>
      subNavItems.value.find((item) =>
        isNavbarItemActive(item, route.path, route.query.returnTo as string | undefined)
      )?.title
  );
}
